import React, { FC, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import apiRoutes from '../../../../config/api/routes';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { ExerciseSeriesInfo } from '../../../../config/api/models/Exercise';
import { useFetch, useUser } from '../../../shared';
import { ConfirmationDialog, IconButton, Icons } from '../../../shared/components';
import { EditSeriesFormDialog, SeriesCardPaper } from './SeriesCard/index';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { UrlParams } from '../../../shared/types';

type Props = {
  data: ExerciseSeriesInfo;
  onDeleteSeries: () => void;
  onPatchSeries: () => void;
  isTrajectoryViewEditMode?: boolean;
};

const SeriesCard: FC<Props> = ({
  data,
  onDeleteSeries,
  onPatchSeries,
  isTrajectoryViewEditMode = false,
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { getPath } = useI18nPath();
  const { bookId, chapterId, trajectoryId, sectionCode, sectionName } = useParams<UrlParams>();
  const { isTeacher } = useUser();
  const log = useDataLogging();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [patchSeriesDialogOpen, setPatchSeriesDialogOpen] = useState(false);

  const [deleteSeriesResponse, deleteSeries, resetDeleteSeries] = useFetch(
    apiRoutes.bookPage(bookId as string, data.id),
    {
      method: 'DELETE',
    }
  );

  useEffect(() => {
    if (deleteSeriesResponse.success) {
      if (isTeacher) {
        log(LogActionTypes.TEACHER_PAGE_DELETE, {
          bookId,
          trajectoryId,
          pageId: data.id,
          type: data.type,
        });
      }
      onDeleteSeries();
      resetDeleteSeries();
    }
  }, [
    deleteSeriesResponse,
    resetDeleteSeries,
    onDeleteSeries,
    isTeacher,
    log,
    bookId,
    trajectoryId,
    data,
  ]);

  const handleDeleteSeries = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenConfirmDelete(true);
  };

  const handleEditSeries = (event: React.MouseEvent) => {
    event.stopPropagation();
    setPatchSeriesDialogOpen(true);
  };

  const handleClickSeriesCard = () => {
    if (!trajectoryId || !bookId || !chapterId) return;

    push(
      getPath(isTrajectoryViewEditMode ? 'trajectoryView' : 'trajectory', {
        bookId,
        chapterId,
        trajectoryId,
        'sectionCode?': sectionCode,
        'sectionName?': sectionName,
        'seriesId?': data.id,
      }),
      {
        addExerciseOrEvaluation: true,
      }
    );
  };

  const handleCloseConfirm = (result: boolean) => {
    setOpenConfirmDelete(false);
    if (result) {
      deleteSeries({ body: data.id });
    }
  };

  const handleClosePatchSeriesDialog = (result: boolean) => {
    setPatchSeriesDialogOpen(false);
    if (result) {
      onPatchSeries();
    }
  };

  return (
    <>
      <SeriesCardPaper onClick={handleClickSeriesCard} elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h3">{data.title}</Typography>
          <div>
            <IconButton
              small
              icon={Icons.EDIT}
              onClick={handleEditSeries}
              tooltip={t('shared.edit')}
            />
            <IconButton
              small
              icon={Icons.DELETE}
              onClick={handleDeleteSeries}
              tooltip={t('shared.delete')}
            />
          </div>
        </Box>
      </SeriesCardPaper>
      <ConfirmationDialog
        open={openConfirmDelete}
        title={t('trajectory.sections.exercises.confirmDeleteSeries.title')}
        description={t('trajectory.sections.exercises.confirmDeleteSeries.description').replace(
          '%title%',
          data.title
        )}
        onClose={handleCloseConfirm}
      />
      <EditSeriesFormDialog
        id={data.id}
        title={data.title}
        open={patchSeriesDialogOpen}
        onCancel={() => handleClosePatchSeriesDialog(false)}
        onConfirm={() => handleClosePatchSeriesDialog(true)}
      />
    </>
  );
};

export default SeriesCard;
